import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Container, Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import NewsFeed from '../components/news/NewsFeed';
import NewsSourceSettings from '../components/news/NewsSourceSettings';
import NewsBookmarkList from '../components/news/NewsBookmarkList';
import NewsRelatedDialog from '../components/news/NewsRelatedDialog';
import NewsDetailSheet from '../components/news/NewsDetailSheet';
import NewsMenu from '../components/news/NewsMenu';
import { fetchNewsSources } from '../services/newsApi';
import { useUser } from '../contexts/UserContext';
import {
  readBookmarks, removeBookmark, toggleBookmark, clearBookmarks,
} from '../utils/newsBookmarks';
import { readSeenIds, markSeen, unmarkSeen, clearSeen } from '../utils/newsSeen';
import {
  readOverrides, setSourceEnabled, enableAllSources, disableAllSources,
  resetToDefaults, toSourceFilter, isSourceEnabled, SourceOverrides,
} from '../utils/newsSourcePrefs';
import type { NewsArticle, NewsTab } from '../types/News';

const VALID_TABS: NewsTab[] = ['trending', 'latest', 'mine', 'bookmarks'];

const NewsPage: React.FC = () => {
  const { isAuthenticated } = useUser();
  const queryClient = useQueryClient();

  // **タブは URL に持たせる。** state だけで持つと、ゲーム詳細へ移動して
  // 戻ってきたときにページが作り直されて話題タブに戻ってしまう。
  // スクロール位置は復元されるのに中身が別のタブ、という食い違いも起きる。
  // URL に入れておけば戻る操作でタブごと復元され、共有もできる
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as NewsTab | null;
  const tab: NewsTab =
    rawTab && VALID_TABS.includes(rawTab)
      // マイゲームは要ログイン。未ログインでURLを直接叩かれたら話題へ倒す
      && !(rawTab === 'mine' && !isAuthenticated)
      ? rawTab
      : 'trending';

  const setTab = useCallback((next: NewsTab) => {
    // replace にしてタブ切り替えを履歴に積まない。
    // 戻るボタンがタブの行き来を辿るのは煩わしい。
    // 既存のパラメータは残す（丸ごと置き換えると他の用途を巻き込む）
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'trending') {
        params.delete('tab');
      } else {
        params.set('tab', next);
      }
      return params;
    }, { replace: true });
  }, [setSearchParams]);
  const [bookmarks, setBookmarks] = useState(() => readBookmarks());
  // 媒体表示の「既定からの差分」。既定は news_sources.default_enabled 側にある
  const [overrides, setOverrides] = useState<SourceOverrides>(() => readOverrides());
  // クラスタの中身を開いている記事。null なら閉じている
  const [relatedOf, setRelatedOf] = useState<number | null>(null);
  // クッション（記事を開く前の詳細シート）
  const [detailOf, setDetailOf] = useState<number | null>(null);
  // 既読。判定を O(1) にするため Set で持つ
  const [seenIds, setSeenIds] = useState<Set<number>>(() => readSeenIds());

  // クッションを開いた時点で確認済みにする。
  // 外部サイトへ遷移したかは問わない（見出しと概要で判断したなら、それも確認）
  const handleOpenDetail = useCallback((articleId: number) => {
    setDetailOf(articleId);
    // localStorage への書き込みは更新関数の外で行う。
    // setState の更新関数は純粋である必要があり、StrictMode では2回呼ばれる。
    // 直前の値は localStorage から読み直せば常に最新
    setSeenIds(markSeen(articleId, readSeenIds()));
  }, []);

  // クッションから個別に既読を切り替える。
  // handleOpenDetail と同様、localStorage の読み書きは更新関数の外で行う
  // （更新関数は純粋である必要があり、StrictMode では2回呼ばれる）
  const handleToggleSeen = useCallback((articleId: number) => {
    const current = readSeenIds();
    setSeenIds(
      current.has(articleId)
        ? unmarkSeen([articleId])
        : markSeen(articleId, current)
    );
  }, []);

  const { data: sources = [] } = useQuery({
    queryKey: ['news-sources'],
    queryFn: fetchNewsSources,
    staleTime: 60 * 60 * 1000,
  });

  const sourceFilter = useMemo(
    () => toSourceFilter(sources, overrides),
    [sources, overrides]
  );

  // 連続でトグルすると組み合わせの数だけ取得が走り、そのたびに一覧が
  // 作り直されて重くなる。落ち着いてから投げる
  const [debouncedFilter, setDebouncedFilter] = useState(sourceFilter);
  const filterKey = sourceFilter.join(',');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(sourceFilter), 350);
    return () => clearTimeout(timer);
    // sourceFilter は毎回新しい配列になるので、中身を表す文字列で比較する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // タブを変えたら先頭へ戻す。前のタブのスクロール位置のまま
  // 別の一覧が出ると、どこを見ているのか分からなくなる
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    window.scrollTo({ top: 0 });
  }, [tab]);
  // 媒体を全部オフにしている状態。取得しても必ず空になるので手前で止める
  const allMuted = sources.length > 0
    && !sources.some((s) => isSourceEnabled(s, overrides));

  // カード側の判定を O(1) にするため Set にしておく
  const bookmarkedIds = useMemo(
    () => new Set(bookmarks.map((b) => b.article_id)),
    [bookmarks]
  );

  const handleToggleBookmark = useCallback((article: NewsArticle) => {
    toggleBookmark(article);
    setBookmarks(readBookmarks());
  }, []);

  const handleRemoveBookmark = useCallback((articleId: number) => {
    removeBookmark(articleId);
    setBookmarks(readBookmarks());
  }, []);

  // --- 表示の管理メニュー ---
  // 読み込み済みの内容は NewsFeed から受け取る。
  // queryClient.getQueryData() を描画中に読むと購読が無いため、
  // 無限スクロールで増えてもここが再描画されず件数が0のままになる
  const [loaded, setLoaded] = useState<{ articleIds: number[]; pages: number }>({
    articleIds: [], pages: 0,
  });

  // 保存タブでは NewsFeed が居ないので通知が来ない。
  // タブを離れた時点で持ち越さないよう明示的に空へ戻す
  useEffect(() => {
    if (tab === 'bookmarks') setLoaded({ articleIds: [], pages: 0 });
  }, [tab]);

  const seenInViewCount = useMemo(
    () => loaded.articleIds.filter((id) => seenIds.has(id)).length,
    [loaded, seenIds]
  );

  const handleResetFeed = useCallback(() => {
    // **removeQueries ではなく resetQueries を使う。**
    // removeQueries はキャッシュから消すだけで、表示中のクエリは
    // 手元の結果を持ったままなので画面が変わらない（別タブへ行って
    // 戻ると初めて反映される、という挙動になっていた）。
    // resetQueries は初期状態へ戻したうえで、表示中のものを取り直す
    queryClient.resetQueries({ queryKey: ['news'] });
    window.scrollTo({ top: 0 });
  }, [queryClient]);

  // マイゲームは要ログイン。未ログインでは出さない
  const tabs: { value: NewsTab; label: string; count?: number }[] = [
    { value: 'trending', label: '話題' },
    { value: 'latest', label: '新着' },
    ...(isAuthenticated ? [{ value: 'mine' as NewsTab, label: 'マイゲーム' }] : []),
    { value: 'bookmarks', label: '保存', count: bookmarks.length },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
      <Helmet>
        <title>ゲームニュース | PULSE</title>
        <meta
          name="description"
          content="ゲームメディアの最新ニュースと話題の記事をまとめてチェック。"
        />
      </Helmet>

      {/* ヘッダー。スクロールしてもタブと媒体設定に手が届くよう貼り付ける */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: 56, sm: 64 },
          zIndex: 2,
          mx: { xs: -2, sm: -3 },
          px: { xs: 2, sm: 3 },
          pt: 0.5,
          pb: 1.25,
          backgroundColor: 'rgba(18,18,18,0.82)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
          <Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.95rem' }, fontWeight: 700, letterSpacing: '.01em' }}>
            ニュース
          </Typography>
          <Box sx={{ flex: 1 }} />
          <NewsSourceSettings
            sources={sources}
            overrides={overrides}
            // 直前の値は localStorage から読み直す。
            // props の overrides を使うと、素早く連打したとき
            // 再レンダリング前の古い値に上書きされて取りこぼす
            onToggle={(source, enabled) =>
              setOverrides(setSourceEnabled(source, enabled, readOverrides()))}
            onResetDefaults={() => setOverrides(resetToDefaults())}
            onEnableAll={() => setOverrides(enableAllSources(sources))}
            onDisableAll={() => setOverrides(disableAllSources(sources))}
          />
          <NewsMenu
            seenCount={seenIds.size}
            seenInViewCount={seenInViewCount}
            bookmarkCount={bookmarks.length}
            loadedPages={loaded.pages}
            onClearSeenInView={() => setSeenIds(unmarkSeen(loaded.articleIds))}
            onClearSeen={() => setSeenIds(clearSeen())}
            onClearBookmarks={() => setBookmarks(clearBookmarks())}
            onResetFeed={handleResetFeed}
          />
        </Box>

        {/* セグメント型のタブ。MUIの下線タブは要素が少ないと間延びして見える */}
        <Box
          sx={{
            display: 'inline-flex',
            gap: 0.25,
            p: 0.375,
            borderRadius: 10,
            bgcolor: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.07)',
            // 「ブックマーク」まで並ぶと狭い端末では収まらない。
            // 折り返さず横スクロールさせる（スクロールバーは隠す）
            maxWidth: '100%',
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {tabs.map((t) => {
            const active = tab === t.value;
            return (
              <Box
                key={t.value}
                onClick={() => setTab(t.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 10,
                  cursor: 'pointer',
                  userSelect: 'none',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                  // 1rem = 20px (index.css)。タブは主要な操作なので 14px 確保する
                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  fontWeight: active ? 700 : 500,
                  color: active ? '#121212' : 'rgba(255,255,255,0.6)',
                  bgcolor: active ? 'primary.main' : 'transparent',
                  transition: 'background-color .15s ease, color .15s ease',
                  '&:hover': active ? {} : { color: 'rgba(255,255,255,0.9)' },
                }}
              >
                {t.label}
                {t.count !== undefined && t.count > 0 && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: { xs: '0.55rem', sm: '0.6rem' },
                      fontWeight: 700,
                      opacity: active ? 0.7 : 0.5,
                    }}
                  >
                    {t.count}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {tab === 'bookmarks' ? (
          <NewsBookmarkList
            bookmarks={bookmarks}
            onRemove={handleRemoveBookmark}
            onOpenDetail={handleOpenDetail}
          />
        ) : (
          <NewsFeed
            tab={tab}
            sources={debouncedFilter}
            bookmarkedIds={bookmarkedIds}
            onToggleBookmark={handleToggleBookmark}
            onOpenRelated={setRelatedOf}
            onOpenDetail={handleOpenDetail}
            onLoadedChange={setLoaded}
            seenIds={seenIds}
            allMuted={allMuted}
            onEnableAll={() => setOverrides(enableAllSources(sources))}
          />
        )}
      </Box>

      <NewsDetailSheet
        articleId={detailOf}
        onClose={() => setDetailOf(null)}
        bookmarked={detailOf != null && bookmarkedIds.has(detailOf)}
        seen={detailOf != null && seenIds.has(detailOf)}
        onToggleBookmark={handleToggleBookmark}
        onToggleSeen={handleToggleSeen}
        onOpenRelated={(id) => { setDetailOf(null); setRelatedOf(id); }}
      />

      <NewsRelatedDialog
        articleId={relatedOf}
        sources={debouncedFilter}
        onClose={() => setRelatedOf(null)}
      />
    </Container>
  );
};

export default NewsPage;
